import cn from 'classnames';
import React from 'react';
import { useReducedMotion } from 'src/shared/hooks/useReducedMotion';
import { Icon, IconName } from 'web-common/src/shared/design-system/components/Icon';

interface GatePerk {
  iconName: IconName;
  title: string;
  description: string;
}

const GATE_PERKS: GatePerk[] = [
  {
    iconName: 'perkExclusiveChefs',
    title: '100+ Local, Top-Tier Chefs',
    description: 'Match with Michelin-starred and James Beard winning chefs.',
  },
  {
    iconName: 'perkGlobalCuisines',
    title: '25+ Global Cuisines',
    description: 'From Italian to Thai, explore unique flavors from around the world!',
  },
  {
    iconName: 'perkFreeDelivery',
    title: 'Free Delivery & Flexibility',
    description: 'Enjoy free delivery weekly, and pause or skip anytime.',
  },
  {
    iconName: 'perkPersonalizedMenu',
    title: 'Your Personalized Menu',
    description: 'Browse a menu curated to your tastes, dietary needs, and goals.',
  },
];

/** Pulls each row back by the border width, so `divide-y` lands the rule on the boundary at 84, not 85. */
const DRAW_RULES_ON_THE_BOUNDARY = '[&>*+*]:-mt-px [&>*:last-child]:-mb-0.5';

/**
 * A white band over a white header, so it only reads where it crosses the glyphs. The 0.55 alpha is
 * design's own value and a known AA exception: at the sweep's peak the header composites to 2.53:1
 * against milk, under 1.4.3's 4.5:1. Bounded by how briefly the band crosses a glyph, and off
 * entirely under reduced motion.
 */
const HEADER_SHEEN = cn(
  "after:pointer-events-none after:absolute after:inset-0 after:w-[40%] after:-translate-x-full after:content-['']",
  'after:bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.55)_50%,transparent_100%)]'
);

export const SignUpGatePerks: React.FC<{ className?: string }> = ({ className }) => {
  const reducedMotion = useReducedMotion();

  return (
    <div className={className}>
      <div
        className={cn('overflow-hidden rounded-xl drop-shadow-[1px_1px_17.65px_rgba(55,11,29,0.25)]', {
          'animate-perks-bob': !reducedMotion,
        })}>
        <div
          className={cn(
            'relative flex items-center justify-center overflow-hidden border-2 border-eggplant-50 bg-milk px-4 py-2.5',
            HEADER_SHEEN,
            { 'after:animate-shimmer-sweep': !reducedMotion, 'after:hidden': reducedMotion }
          )}>
          {/* JA-WEIGHT-SHIM(1218377610766475): font-[600] renders Ja Bold under the shifted map; drop it for text-caption-700-caps when the map lands */}
          <h3 className='text-caption-700-caps font-[600] text-eggplant-500 text-box-trim-cap'>
            What&rsquo;s waiting for you
          </h3>
        </div>
        <div
          className={cn(
            'divide-y divide-eggplant-50 border-x-2 border-b-2 border-eggplant-50 bg-milk',
            DRAW_RULES_ON_THE_BOUNDARY
          )}>
          {GATE_PERKS.map(({ iconName, title, description }) => (
            <div key={title} className='flex items-center gap-4 px-4 py-3'>
              <span className='flex size-[60px] shrink-0 items-center justify-center rounded-full bg-rice'>
                <Icon name={iconName} size={32} />
              </span>
              <div className='flex flex-col gap-2.5'>
                {/* JA-WEIGHT-SHIM(1218377610766475): font-[600] renders Ja Bold under the shifted map; drop it for text-caption-700 when the map lands */}
                <p className='text-caption-700 font-[600] text-eggplant text-box-trim-cap'>{title}</p>
                <p className='text-caption-400 text-eggplant-500 text-box-trim-cap'>{description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
